import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined"
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined"
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOnOutlined"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import TableRestaurantOutlinedIcon from "@mui/icons-material/TableRestaurantOutlined"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import Column from "../../../../common/components/Column"
import { useCustomPerksState } from "../../utils"
import CheckableChip from "./CheckableChip"
import ChipContainer from "./ChipContainer"
import CustomPerkDialog from "./CustomPerkDialog"
import IconValue from "./IconValue"

export default function PerksEditor({
  selectedPerks,
  setSelectedPerks,
  selectedCustomPerks,
  setSelectedCustomPerks,
}) {
  const [perksExpandedSection, setPerksExpandedSection] = useState(
    PERKS_CATEGORIES.compensation
  )
  const [allPerks, setAllPerks] = useState([])
  const {
    customPerks,
    openCustomPerkDialog,
    handleCustomPerkDialogClosed,
    customPerkDialogOpen,
  } = useCustomPerksState()
  const axios = useAuthorizedAxiosClient()

  useEffect(
    () => {
      axios.get("/crm/api/perks/").then(({ data }) => {
        setAllPerks(data)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <>
      {[
        PERKS_CATEGORIES.compensation,
        PERKS_CATEGORIES.admin,
        PERKS_CATEGORIES.office,
        PERKS_CATEGORIES.marketing,
      ].map((category, i) => (
        <PerksAccordionSection
          key={i}
          category={category}
          expandedSection={perksExpandedSection}
          setExpandedSection={setPerksExpandedSection}
        >
          <PerkChips
            category={category}
            allPerks={allPerks}
            selectedPerks={selectedPerks}
            setSelectedPerks={setSelectedPerks}
          />
        </PerksAccordionSection>
      ))}
      <PerksAccordionSection
        category={PERKS_CATEGORIES.custom}
        expandedSection={perksExpandedSection}
        setExpandedSection={setPerksExpandedSection}
      >
        {customPerks.length === 0 ? (
          <Column sx={{ alignItems: "center", gap: 0 }}>
            <Typography variant="body1" color="text.secondary">
              No custom perks created yet.
            </Typography>
            <Button
              startIcon={<AddOutlinedIcon />}
              color="primary"
              variant="text"
              size="small"
              onClick={openCustomPerkDialog}
            >
              Add custom perk
            </Button>
          </Column>
        ) : (
          <PerkChips
            category={PERKS_CATEGORIES.custom}
            allPerks={customPerks}
            selectedPerks={selectedCustomPerks}
            setSelectedPerks={setSelectedCustomPerks}
          >
            <IconButton size="small" onClick={openCustomPerkDialog}>
              <AddOutlinedIcon />
            </IconButton>
          </PerkChips>
        )}
        <CustomPerkDialog
          open={customPerkDialogOpen}
          onClose={handleCustomPerkDialogClosed}
        />
      </PerksAccordionSection>
    </>
  )
}

function PerksAccordionSection({
  category,
  expandedSection,
  setExpandedSection,
  children,
}) {
  const Icon = getIconForPerkCategory(category)

  function handlePerksSectionExpanded(e, expanded) {
    setExpandedSection(expanded ? category : null)
  }

  return (
    <Accordion
      elevation={0}
      expanded={category === expandedSection}
      onChange={handlePerksSectionExpanded}
      sx={{ outline: "none", "&.MuiPaper-root": { width: "100%" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
        <IconValue Icon={Icon} value={SECTION_TITLES[category]} />
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  )
}

function PerkChips({
  category,
  allPerks,
  selectedPerks,
  setSelectedPerks,
  children,
}) {
  function filterPerksByCategory(category) {
    return allPerks.filter(
      (perk) => /* custom perk */ !perk.category || perk.category === category
    )
  }

  function handlePerkChipSelectedWrapper(perk) {
    return function () {
      setSelectedPerks([...selectedPerks, perk])
    }
  }

  function handlePerkChipDeselectedWrapper(perkID) {
    return function () {
      setSelectedPerks(selectedPerks.filter(({ id }) => id !== perkID))
    }
  }

  return (
    <ChipContainer>
      {filterPerksByCategory(PERKS_CATEGORIES[category]).map((perk) => (
        <CheckableChip
          key={perk.id}
          label={perk.title}
          checked={selectedPerks.findIndex((p) => p.id === perk.id) !== -1}
          onChecked={handlePerkChipSelectedWrapper(perk)}
          onUnchecked={handlePerkChipDeselectedWrapper(perk.id)}
        />
      ))}
      {children}
    </ChipContainer>
  )
}

export function useSelectedPerks(initialPerks = []) {
  const [selectedPerks, setSelectedPerks] = useState(initialPerks)
  return { selectedPerks, setSelectedPerks }
}

export const PERKS_CATEGORIES = {
  compensation: "compensation",
  admin: "admin",
  office: "office",
  marketing: "marketing",
  custom: "custom",
}

const SECTION_TITLES = {
  [PERKS_CATEGORIES.compensation]: "Compensation",
  [PERKS_CATEGORIES.admin]: "Admin support",
  [PERKS_CATEGORIES.office]: "Office",
  [PERKS_CATEGORIES.marketing]: "Marketing & leads",
  [PERKS_CATEGORIES.custom]: "Custom perks",
}

export function getIconForPerkCategory(category) {
  switch (category) {
    case PERKS_CATEGORIES.compensation:
      return MonetizationOnIcon
    case PERKS_CATEGORIES.admin:
      return ContactSupportOutlinedIcon
    case PERKS_CATEGORIES.office:
      return TableRestaurantOutlinedIcon
    case PERKS_CATEGORIES.marketing:
      return CampaignOutlinedIcon
    default:
      return SettingsOutlinedIcon
  }
}
